import React from 'react';

interface AtomicLoaderProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const AtomicLoader: React.FC<AtomicLoaderProps> = ({ 
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12', 
    lg: 'w-16 h-16'
  };

  return (
    <div className={`relative ${sizeClasses[size]} ${className}`}>
      {/* Átomo central - núcleo radioativo */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="w-3 h-3 bg-gradient-to-r from-green-400 via-emerald-500 to-cyan-400 rounded-full animate-pulse shadow-lg shadow-green-400/50">
          <div className="absolute inset-0 bg-gradient-to-r from-green-300 to-cyan-300 rounded-full animate-ping"></div>
        </div>
      </div>

      {/* Órbita 1 - Elétron 1 */}
      <div className="absolute inset-0 animate-spin" style={{ animationDuration: '2s' }}>
        <div className="relative w-full h-full border border-green-400/30 rounded-full">
          <div className="absolute w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full shadow-md shadow-cyan-400/50 animate-pulse"
               style={{ 
                 top: '-4px', 
                 left: '50%', 
                 transform: 'translateX(-50%)',
                 filter: 'drop-shadow(0 0 4px #22d3ee)'
               }}>
          </div>
        </div>
      </div>

      {/* Órbita 2 - Elétron 2 (rotação oposta) */}
      <div className="absolute inset-0 animate-spin" style={{ 
        animationDuration: '3s', 
        animationDirection: 'reverse',
        transform: 'rotate(60deg)'
      }}>
        <div className="relative w-full h-full border border-emerald-400/30 rounded-full">
          <div className="absolute w-2 h-2 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full shadow-md shadow-emerald-400/50 animate-pulse"
               style={{ 
                 top: '-4px', 
                 left: '50%', 
                 transform: 'translateX(-50%)',
                 filter: 'drop-shadow(0 0 4px #10b981)'
               }}>
          </div>
        </div>
      </div>

      {/* Órbita 3 - Elétron 3 */}
      <div className="absolute inset-0 animate-spin" style={{ 
        animationDuration: '2.5s',
        transform: 'rotate(-60deg)'
      }}>
        <div className="relative w-full h-full border border-teal-400/30 rounded-full">
          <div className="absolute w-2 h-2 bg-gradient-to-r from-teal-400 to-cyan-500 rounded-full shadow-md shadow-teal-400/50 animate-pulse"
               style={{ 
                 top: '-4px', 
                 left: '50%', 
                 transform: 'translateX(-50%)',
                 filter: 'drop-shadow(0 0 4px #14b8a6)'
               }}>
          </div>
        </div>
      </div>

      {/* Brilho radioativo de fundo */}
      <div className="absolute inset-0 bg-gradient-radial from-green-400/20 via-emerald-400/10 to-transparent rounded-full animate-pulse"
           style={{ animationDuration: '1.5s' }}>
      </div>

      {/* Efeito de radiação */}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-3 bg-gradient-to-t from-green-400/80 to-transparent opacity-30 animate-pulse"
            style={{
              top: '50%',
              left: '50%',
              transformOrigin: '2px 6px',
              transform: `translate(-50%, -50%) rotate(${i * 45}deg)`,
              animationDelay: `${i * 0.1}s`,
              animationDuration: '2s'
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Componente de Loading Screen completo
export const AtomicLoadingScreen: React.FC<{
  message?: string;
  className?: string;
}> = ({ 
  message = "Carregando...",
  className = ""
}) => {
  return (
    <div className={`flex flex-col items-center justify-center space-y-4 ${className}`}>
      <AtomicLoader size="lg" />
      <div className="text-center space-y-2">
        <p className="text-lg font-semibold text-gray-700 animate-pulse">
          {message}
        </p>
        <div className="flex space-x-1 justify-center">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-bounce"
              style={{ 
                animationDelay: `${i * 0.2}s`,
                filter: 'drop-shadow(0 0 2px #10b981)'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AtomicLoader; 